import Box from '@mui/material/Box';
import Radio from '@mui/material/Radio';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import OutlinedInput from '@mui/material/OutlinedInput';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { MESSAGES } from 'src/constants/messages';

import { TargetComboInput } from './TargetComboInput';
import { useMoveCoverageForm } from '../hooks/useMoveCoverageForm';

// ----------------------------------------------------------------------

type MoveCoverageModalProps = {
  open: boolean;
  onClose: () => void;
  comboId: number;
  coverageId?: number;
  coverageIds?: number[];
  onBatchMoveSuccess?: () => void;
  originPage?: 'coverage' | 'combo';
};

export function MoveCoverageModal({
  open,
  onClose,
  comboId,
  coverageId,
  coverageIds,
  onBatchMoveSuccess,
  originPage = 'coverage',
}: Readonly<MoveCoverageModalProps>) {
  const {
    isBatch,
    moveType,
    moveTypeError,
    targetComboIdInput,
    reason,
    comboValidationError,
    reasonError,
    isSaving,
    isTargetComboResolved,
    resolvedTargetComboName,
    isComboLookupLoading,
    systemNotePrefix,
    handleSave,
    handleClose,
    handleMoveTypeChange,
    handleTargetComboIdChange,
    handleClearTargetComboId,
    handleReasonChange,
    validateTargetCombo,
    validateReason,
  } = useMoveCoverageForm({ comboId, coverageId, coverageIds, onClose, onBatchMoveSuccess, originPage });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      data-testid="move-coverage-modal"
      sx={{
        '& .MuiDialog-paper': { maxWidth: 720 },
        '& .MuiDialogTitle-root + .MuiDialogContent-root': { pt: 3 },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          py: 2,
          borderBottom: '1px dashed',
          borderColor: 'divider',
        }}
      >
        {isBatch ? `Move ${coverageIds!.length} coverages` : 'Move coverage'}
        <IconButton
          onClick={handleClose}
          aria-label="close"
          data-testid="move-coverage-close-btn"
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 3, pb: 3 }}>
        <RadioGroup value={moveType ?? ''} onChange={handleMoveTypeChange}>
          {/* Option 1: Existing combo */}
          <FormControlLabel
            value="existing"
            control={<Radio />}
            label={MESSAGES.COVERAGE.MOVE_MODAL.EXISTING_COMBO_LABEL}
            data-testid="radio-existing-combo"
            sx={{ mb: 1 }}
          />
          {moveType === 'existing' && (
            <TargetComboInput
              value={targetComboIdInput}
              onChange={handleTargetComboIdChange}
              onBlur={() => validateTargetCombo(true)}
              onClear={handleClearTargetComboId}
              error={comboValidationError}
              isLoading={isComboLookupLoading}
              isResolved={isTargetComboResolved}
              resolvedComboName={resolvedTargetComboName}
            />
          )}

          {/* Option 2: New combo */}
          <FormControlLabel
            value="new-combo"
            control={<Radio />}
            label={MESSAGES.COVERAGE.MOVE_MODAL.NEW_COMBO_LABEL}
            data-testid="radio-new-combo"
          />
        </RadioGroup>
        {moveTypeError && (
          <Typography variant="caption" color="error" data-testid="move-coverage-move-type-error">
            {moveTypeError}
          </Typography>
        )}

        <Divider sx={{ my: 3, borderStyle: 'dashed' }} />

        {/* Reason textarea - always visible */}
        <Box>
          {moveType && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}
              data-testid="system-note-prefix"
            >
              {systemNotePrefix}
            </Typography>
          )}

          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            {MESSAGES.COVERAGE.MOVE_MODAL.REASON_LABEL}
            <Box component="span" sx={{ color: 'error.main' }}>
              *
            </Box>
          </Typography>
          <OutlinedInput
            value={reason}
            onChange={(e) => handleReasonChange(e.target.value)}
            onBlur={() => validateReason(true)}
            multiline
            rows={4}
            fullWidth
            placeholder=""
            data-testid="move-coverage-reason"
            error={!!reasonError}
          />
          {reasonError && (
            <Typography variant="caption" color="error" data-testid="move-coverage-reason-error">
              {reasonError}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', opacity: 0.8 }} />
            <Typography variant="body2" color="text.secondary">
              Consider including the context and reason for your action.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: 'space-between',
          px: 3,
          pb: 3,
          pt: 2,
          borderTop: '1px dashed',
          borderColor: 'divider',
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          color="inherit"
          data-testid="move-coverage-cancel-btn"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="inherit"
          onClick={handleSave}
          disabled={isSaving}
          data-testid="move-coverage-save-btn"
          sx={{ bgcolor: 'grey.800', color: 'common.white', '&:hover': { bgcolor: 'grey.700' } }}
        >
          {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
